/**
 * Main JavaScript entry point
 *
 * This imports all necessary modules and styles
 */

// Import main stylesheet
import "../scss/style.scss";

// Import Lenis for smooth scrolling
import Lenis from "@studio-freight/lenis";

// Import GSAP for ScrollTrigger integration
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Import custom modules
import initNavScroll from "./modules/nav-scroll";
import initMobileNav from "./modules/mobile-nav";
import initAccordion from "./modules/accordion";
import initLeadershipModal from "./modules/leadership-modal";
import initFullPageCarousel from "./modules/full-page-carousel";
import initPageTakeOverVideo from "./modules/page-take-over-video";
import initImageCarousel from "./modules/image-carousel";

// Modals

// Component Animations

// DOM ready
document.addEventListener("DOMContentLoaded", function () {
  // Register ScrollTrigger plugin
  gsap.registerPlugin(ScrollTrigger);

  // Initialize Lenis smooth scrolling
  const lenis = initLenis();
  // Make lenis available globally for other modules
  window.lenis = lenis;

  // Configure ScrollTrigger to work with Lenis
  ScrollTrigger.scrollerProxy(document.body, {
    scrollTop(value) {
      return arguments.length
        ? lenis.scrollTo(value, 0, 0)
        : lenis.animatedScroll;
    },
    getBoundingClientRect() {
      return {
        top: 0,
        left: 0,
        width: window.innerWidth,
        height: window.innerHeight,
      };
    },
    pinType: document.body.style.transform ? "transform" : "fixed",
  });

  // Initialize modules
  initNavScroll();
  initMobileNav();
  initAccordion();
  initLeadershipModal();
  initFullPageCarousel();
  initPageTakeOverVideo();
  initImageCarousel();

  // Refresh ScrollTrigger after all animations are initialized
  ScrollTrigger.refresh();

  setTimeout(() => {
    if (window.location.hash && window.scrollToElement) {
      window.scrollToElement(window.location.hash);
    }
  }, 100);

  // Update ScrollTrigger on window resize
  window.addEventListener("resize", () => {
    ScrollTrigger.refresh();
  });
});

/**
 * Initialize Lenis smooth scrolling with pinned section support
 */
function initLenis() {
  // Initialize Lenis with options
  const lenis = new Lenis({
    duration: 1.2,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    direction: "vertical",
    gestureDirection: "vertical",
    smooth: true,
    mouseMultiplier: 1,
    smoothTouch: false,
    touchMultiplier: 2,
    infinite: false,
  });

  // Get all links that hash (same-page navigation)

  // Connect lenis to RAF (request animation frame)
  function raf(time) {
    lenis.raf(time);
    requestAnimationFrame(raf);
  }

  requestAnimationFrame(raf);

  // Connect lenis to scroll events to update scroll trigger animations
  lenis.on("scroll", ScrollTrigger.update);

  // Add data attributes to body for potential styling during smooth scrolling
  function lenisStyles() {
    if (lenis.isStopped) {
      document.body.setAttribute("data-lenis-stopped", "");
    } else {
      document.body.removeAttribute("data-lenis-stopped");
    }

    if (lenis.isScrolling) {
      document.body.setAttribute("data-lenis-scrolling", "");
    } else {
      document.body.removeAttribute("data-lenis-scrolling");
    }

    if (lenis.direction === 1) {
      document.body.setAttribute("data-lenis-scrolling-down", "");
      document.body.removeAttribute("data-lenis-scrolling-up");
    } else if (lenis.direction === -1) {
      document.body.setAttribute("data-lenis-scrolling-up", "");
      document.body.removeAttribute("data-lenis-scrolling-down");
    }
  }

  lenis.on("scroll", lenisStyles);

  // Handle initial hash after ScrollTrigger is ready

  return lenis;
}
